// components/CronologiaPanel.tsx — V29 S1.
//
// The conversation history, moved out of the deleted LeftDrawer into a
// slide-over panel owned by Il Prof (opened from the clock icon in the chrome).
// Ports the drawer's W3 Phase A/C behavior verbatim — last 20 sessions,
// `chiedi` only, async resume with double-tap guard, fetch-failure fallback to
// the true empty thread — plus V29's consecutive-duplicate dedup: back-to-back
// sessions with the same summary collapse to the newest one (the lab student's
// "cos'è il segnale di stop?" ×3 problem).

import { useEffect, useState, type JSX } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTokens } from '../state/theme';
import { useReducedMotion } from '../state/use-reduced-motion';
import { sessionsApi, type RecentSession } from '../api/sessions';
import { relativeTimeIt } from '../utils/relative-time-it';
import { resumeProfSession } from '../state/prof-resume';
import { useChat } from '../state/chat';

type CronologiaPhase =
  | { kind: 'loading' }
  | { kind: 'error' }
  | { kind: 'ready'; sessions: RecentSession[] };

/** Collapse CONSECUTIVE sessions sharing the same trimmed summary (keep newest). */
export function dedupeConsecutive(sessions: RecentSession[]): RecentSession[] {
  const out: RecentSession[] = [];
  for (const s of sessions) {
    const prev = out[out.length - 1];
    if (prev && prev.summary.trim() === s.summary.trim()) continue;
    out.push(s);
  }
  return out;
}

interface CronologiaPanelProps {
  visible: boolean;
  onClose: () => void;
}

export function CronologiaPanel({ visible, onClose }: CronologiaPanelProps): JSX.Element {
  const tokens = useTokens();
  const navigate = useNavigate();
  const reducedMotion = useReducedMotion();
  const [resumingSessionId, setResumingSessionId] = useState<string | null>(null);
  const [phase, setPhase] = useState<CronologiaPhase>({ kind: 'loading' });

  useEffect(() => {
    if (!visible) return;
    let alive = true;
    setPhase({ kind: 'loading' });
    void sessionsApi.recent(20)
      .then((p) => {
        if (alive) setPhase({
          kind: 'ready',
          sessions: dedupeConsecutive(p.sessions.filter((s) => s.mode === 'chiedi')),
        });
      })
      .catch(() => { if (alive) setPhase({ kind: 'error' }); });
    return () => { alive = false; };
  }, [visible]);

  const transition = reducedMotion
    ? 'none'
    : 'transform 0.28s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.28s cubic-bezier(0.22, 1, 0.36, 1)';

  return (
    <div
      aria-hidden={!visible}
      style={{
        position:      'absolute',
        inset:         0,
        zIndex:        200,
        display:       'flex',
        flexDirection: 'row-reverse',
        pointerEvents: visible ? 'auto' : 'none',
      }}
    >
      {/* Panel — slides in from the right (it belongs to the chrome's right-side
          clock action), 84% width so tap-out is obvious. */}
      <div
        data-testid="cronologia-panel"
        role="dialog"
        aria-label="Cronologia delle conversazioni"
        style={{
          width:         '84%',
          maxWidth:      340,
          background:    tokens.drawerBg,
          display:       'flex',
          flexDirection: 'column',
          height:        '100%',
          borderLeft:    `1px solid ${tokens.line}`,
          transform:     visible ? 'translateX(0)' : 'translateX(104%)',
          transition,
          willChange:    'transform',
        }}
      >
        <div
          style={{
            display:       'flex',
            alignItems:    'center',
            justifyContent:'space-between',
            padding:       'calc(var(--sai-top) + 14px) 8px 8px 16px',
            borderBottom:  `1px solid ${tokens.line}`,
          }}
        >
          <div
            style={{
              fontFamily:    tokens.fonts.mono,
              fontSize:      11,
              color:         tokens.textMuted,
              letterSpacing: 0.4,
              textTransform: 'uppercase',
              fontWeight:    700,
            }}
          >
            Cronologia
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Chiudi cronologia"
            style={{
              width: 44, height: 44, border: 'none', background: 'transparent',
              cursor: 'pointer', color: tokens.textMuted,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div
          data-testid="cronologia-body"
          style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '10px 8px' }}
        >
          {phase.kind === 'loading' && (
            <div
              data-testid="cronologia-loading"
              style={{ fontFamily: tokens.fonts.body, fontSize: 12.5, color: tokens.textMuted, padding: '6px 10px' }}
            >
              Sto caricando le tue sessioni…
            </div>
          )}

          {phase.kind === 'error' && (
            <div
              data-testid="cronologia-error"
              role="alert"
              style={{ fontFamily: tokens.fonts.body, fontSize: 12.5, color: tokens.text, padding: '6px 10px', lineHeight: 1.4 }}
            >
              Non sono riuscito a caricare la cronologia. Riprova tra poco.
            </div>
          )}

          {phase.kind === 'ready' && phase.sessions.length === 0 && (
            <div
              data-testid="cronologia-empty"
              style={{ fontFamily: tokens.fonts.body, fontSize: 12.5, color: tokens.textMuted, padding: '6px 10px', lineHeight: 1.4 }}
            >
              Nessuna conversazione recente. Scrivi a Il Prof per vedere qui la cronologia.
            </div>
          )}

          {phase.kind === 'ready' && phase.sessions.length > 0 && (
            <ul data-testid="cronologia-list" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
              {phase.sessions.map((s) => {
                const relTime = relativeTimeIt(s.started_at);
                const ariaText = relTime
                  ? `Riprendi la conversazione: ${s.summary}, ${relTime}`
                  : `Riprendi la conversazione: ${s.summary}`;
                const isLoading = resumingSessionId === s.id;

                const handleClick = () => {
                  if (isLoading) return;
                  // Async resume: fetch messages, hydrate the store, navigate.
                  // On failure we clear the cached thread + sessionId BEFORE
                  // navigating so Il Prof renders its true empty state — the
                  // recovery signal per the W3 UX rubric for expired sessions.
                  setResumingSessionId(s.id);
                  void resumeProfSession(s.id)
                    .catch(() => {
                      useChat.getState().setChiediSessionId(null);
                      useChat.getState().clearThread('chiedi');
                    })
                    .finally(() => {
                      setResumingSessionId(null);
                      onClose();
                      navigate('/prof');
                    });
                };

                return (
                  <li key={s.id} style={{ listStyle: 'none', margin: 0 }}>
                    <button
                      type="button"
                      onClick={handleClick}
                      disabled={isLoading}
                      aria-label={ariaText}
                      aria-busy={isLoading || undefined}
                      data-testid="cronologia-item"
                      style={{
                        display:      'flex',
                        alignItems:   'flex-start',
                        gap:          10,
                        padding:      '8px 10px',
                        minHeight:    44,
                        borderRadius: 8,
                        cursor:       isLoading ? 'wait' : 'pointer',
                        background:   'transparent',
                        border:       'none',
                        textAlign:    'left',
                        width:        '100%',
                        color:        tokens.text,
                        transition:   'background 0.12s ease',
                      }}
                      onMouseEnter={(e) => { if (!isLoading) e.currentTarget.style.background = tokens.drawerHover; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                    >
                      <span style={{ flex: '0 0 auto', marginTop: 1, color: tokens.textMuted }}>
                        <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M4 6a2 2 0 012-2h8a2 2 0 012 2v5a2 2 0 01-2 2h-3l-3 3v-3H6a2 2 0 01-2-2V6z" />
                        </svg>
                      </span>
                      <span style={{ flex: 1, minWidth: 0 }}>
                        <span
                          style={{
                            display:      'block',
                            fontFamily:   tokens.fonts.body,
                            fontSize:     12.5,
                            fontWeight:   500,
                            color:        tokens.text,
                            lineHeight:   1.35,
                            overflow:     'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace:   'nowrap',
                          }}
                        >
                          {s.summary}
                        </span>
                        {relTime && (
                          <span
                            style={{
                              display:    'block',
                              fontFamily: tokens.fonts.mono,
                              fontSize:   10,
                              color:      tokens.textFaint,
                              marginTop:  2,
                            }}
                          >
                            {relTime}
                          </span>
                        )}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      {/* Scrim — tap to close. */}
      <div
        onClick={onClose}
        style={{
          flex:          1,
          background:    'rgba(0,0,0,0.38)',
          opacity:       visible ? 1 : 0,
          transition,
          pointerEvents: visible ? 'auto' : 'none',
        }}
        aria-label="Chiudi cronologia"
        role="button"
        tabIndex={-1}
      />
    </div>
  );
}
